import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Sparkles, Clock, TrendingUp, Users, Play } from "lucide-react";
import { Reveal, MaskedLines, FadeIn } from "@/components/Reveal";
import { FEATURES, FEATURE_CATEGORIES, USE_CASES, IMAGES, REGISTER_URL, GET_STARTED_URL } from "@/data/site";

const trusted = ["Harmony Music Academy", "Elite Chess Club", "Urban Dance Co.", "Peak Sports Hub", "Zen Yoga Centre", "Bright Minds Tuition", "Canvas Art School", "Apex Coaching"];

const stats = [
  { icon: Clock, value: "12h", label: "saved every week on admin" },
  { icon: TrendingUp, value: "98%", label: "on-time fee collection" },
  { icon: Users, value: "500+", label: "institutes onboarded" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-36 pb-20 lg:pt-44">
        <div className="radial-glow pointer-events-none absolute inset-0" />
        <div className="grid-bg pointer-events-none absolute inset-0 opacity-60 [mask-image:radial-gradient(circle_at_50%_0%,black,transparent_70%)]" />

        <div className="relative mx-auto max-w-5xl px-5 text-center lg:px-8">
          <FadeIn className="mb-6 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-zinc-300">
              <Sparkles className="h-3.5 w-3.5 text-brand" />
              The all-in-one platform for activity institutes
            </span>
          </FadeIn>

          <h1 className="font-heading text-5xl font-bold leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl">
            <MaskedLines lines={["Run your academy", "without the busywork."]} />
          </h1>

          <FadeIn delay={0.5}>
            <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-zinc-400">
              Activity Hub Manager brings students, teachers, attendance, reminders and fees
              into one beautiful cloud dashboard — so you can spend less time on admin and
              more time teaching.
            </p>
          </FadeIn>

          <FadeIn delay={0.65}>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={REGISTER_URL}
                data-testid="hero-start-trial-btn"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover sm:w-auto"
              >
                Start Free Trial
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                to="/features"
                data-testid="hero-explore-btn"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto"
              >
                <Play className="h-4 w-4" /> Explore Features
              </Link>
            </div>
          </FadeIn>
        </div>

        {/* Product screenshot with parallax + glow */}
        <div className="relative mx-auto mt-16 max-w-6xl px-5 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-x-10 -top-10 bottom-0 -z-10 bg-[radial-gradient(circle_at_50%_0%,rgba(0,85,255,0.35),transparent_60%)] blur-2xl" />
            <div className="overflow-hidden rounded-xl border border-zinc-800 bg-surface shadow-2xl">
              <div className="flex items-center gap-2 border-b border-zinc-800 bg-black/40 px-4 py-3">
                <span className="h-3 w-3 rounded-full bg-zinc-700" />
                <span className="h-3 w-3 rounded-full bg-zinc-700" />
                <span className="h-3 w-3 rounded-full bg-zinc-700" />
                <span className="ml-3 text-xs text-zinc-500">app.activityhubmanager.com/dashboard</span>
              </div>
              <div className="relative aspect-[16/9] overflow-hidden">
                <motion.img
                  src={IMAGES.heroDashboard}
                  alt="Activity Hub Manager dashboard preview"
                  style={{ y: imgY, scale: imgScale }}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUSTED MARQUEE */}
      <section className="border-y border-white/10 bg-ink py-8" data-testid="trusted-marquee">
        <p className="mb-6 text-center text-xs font-medium uppercase tracking-[0.25em] text-zinc-500">
          Trusted by 500+ activity institutes worldwide
        </p>
        <Marquee gradient gradientColor="#0A0A0A" gradientWidth={120} speed={40} autoFill>
          {trusted.map((name) => (
            <span key={name} className="mx-10 font-heading text-xl font-semibold text-zinc-600 transition-colors hover:text-zinc-300">
              {name}
            </span>
          ))}
        </Marquee>
      </section>

      {/* STATS */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <div className="grid gap-6 md:grid-cols-3">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <div className="rounded-xl border border-zinc-800 bg-surface p-8 transition-colors hover:border-brand/60">
                <s.icon className="h-6 w-6 text-brand" />
                <p className="mt-6 font-heading text-5xl font-bold tracking-tighter text-white">{s.value}</p>
                <p className="mt-2 text-sm text-zinc-400">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURES PREVIEW */}
      <section className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <Reveal className="mb-14 max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-brand">Everything you need</p>
          <h2 className="font-heading text-4xl font-bold tracking-tighter text-white sm:text-5xl">
            One platform, every part of your institute.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.slice(0, 6).map((f, i) => (
            <Reveal key={f.title} delay={(i % 3) * 0.08} className={f.span}>
              <div className="group h-full rounded-xl border border-zinc-800 bg-surface p-8 transition-all duration-300 hover:-translate-y-1 hover:border-brand/70 hover:shadow-[0_0_40px_rgba(0,85,255,0.12)]">
                <div className="flex h-11 w-11 items-center justify-center rounded-md border border-zinc-700 bg-black/40 text-brand transition-colors group-hover:border-brand">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 font-heading text-lg font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10">
          <Link to="/features" data-testid="home-all-features-link" className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-hover">
            Explore all features <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      {/* CAPABILITIES BY CATEGORY */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <Reveal className="mb-12 max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-brand">The complete toolkit</p>
          <h2 className="font-heading text-4xl font-bold tracking-tighter text-white sm:text-5xl">
            Six areas. One simple platform.
          </h2>
          <p className="mt-5 text-zinc-400">
            Activity Hub Manager brings people, attendance, classes, flexible fees, communication
            and insights together — flexible enough for any activity institution, simple enough
            for a small owner to run.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FEATURE_CATEGORIES.map((c, i) => (
            <Reveal key={c.id} delay={(i % 3) * 0.08}>
              <Link
                to="/features"
                data-testid={`home-category-${c.id}`}
                className="group flex h-full flex-col rounded-xl border border-zinc-800 bg-surface p-8 transition-all duration-300 hover:-translate-y-1 hover:border-brand/70"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md border border-zinc-700 bg-black/40 text-brand transition-colors group-hover:border-brand">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-600">
                    {c.items.length} tools
                  </span>
                </div>
                <h3 className="mt-6 font-heading text-xl font-semibold text-white">{c.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-400">{c.blurb}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                  Learn more <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SCREENSHOT SHOWCASE */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-brand">The dashboard</p>
            <h2 className="font-heading text-4xl font-bold tracking-tighter text-white sm:text-5xl">
              A command centre built for clarity.
            </h2>
            <p className="mt-6 max-w-lg text-zinc-400">
              Track attendance, batches and student progress at a glance. Fee reminders trigger
              on your own cycle — by class count, monthly or per class — and class reminders keep
              everyone on schedule.
            </p>
            <ul className="mt-8 space-y-3">
              {["Real-time attendance & class confirmation", "Flexible fee cycles you define — never fixed", "Exportable reports for accounting", "Secure, role-based multi-institution access"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-zinc-300">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand/15 text-brand">✓</span>
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-6 -z-10 bg-[radial-gradient(circle,rgba(0,85,255,0.2),transparent_70%)] blur-2xl" />
              <div className="overflow-hidden rounded-xl border border-zinc-800">
                <img src={IMAGES.product1} alt="Reports view" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-6 hidden w-52 overflow-hidden rounded-lg border border-zinc-700 shadow-2xl sm:block">
                <img src={IMAGES.product2} alt="Attendance view" className="aspect-square w-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHO PREVIEW */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-brand">Built for every discipline</p>
            <h2 className="font-heading text-4xl font-bold tracking-tighter text-white sm:text-5xl">Who can use Activity Hub?</h2>
          </div>
          <Link to="/who-can-use" className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-hover">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {USE_CASES.slice(0, 8).map((u, i) => (
            <Reveal key={u.title} delay={(i % 4) * 0.06}>
              <div className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-surface px-5 py-4 transition-colors hover:border-brand/60">
                <u.icon className="h-5 w-5 shrink-0 text-brand" />
                <span className="text-sm font-medium text-white">{u.title}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-zinc-800 bg-surface p-10 text-center lg:p-20">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(0,85,255,0.25),transparent_60%)]" />
            <div className="relative">
              <h2 className="mx-auto max-w-2xl font-heading text-4xl font-bold tracking-tighter text-white sm:text-5xl">
                Ready to simplify your institute?
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-zinc-400">
                Start your free 14-day trial today. No credit card required.
              </p>
              <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <a href={GET_STARTED_URL} data-testid="cta-get-started-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover sm:w-auto">
                  Get Started <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/contact" data-testid="cta-contact-btn" className="inline-flex w-full items-center justify-center rounded-md border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto">
                  Talk to Sales
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
